import { Container, Graphics } from 'pixi.js';
import { TileSprite } from './TileSprite';
import { WarriorSprite } from './WarriorSprite';
import { COLORS } from '../constants/theme';
import { isRemoteWarrior, type ViewPlayers } from '../ViewState';
import type { BoardPosition, WarriorInstance } from '@/types';
import type { LayoutMetrics } from '../LayoutManager';

const TILE_GAP = 4;
const BOARD_SIZE = 5;

/**
 * Flip row for visual display when local player is player2 (guest).
 * This makes the guest see their warriors at the bottom of the board.
 */
function getVisualRow(row: number, flipPerspective: boolean): number {
  return flipPerspective ? BOARD_SIZE - 1 - row : row;
}

export interface BoardContainerOptions {
  metrics: LayoutMetrics;
  onTileClick: (position: BoardPosition) => void;
  onAttack?: () => void;
  onHeal?: () => void;
  /** View players for determining which warriors are remote (rotated) */
  viewPlayers: ViewPlayers;
}

/**
 * Container for the 5x5 game board
 */
export class BoardContainer extends Container {
  private background: Graphics;
  private tiles: TileSprite[][] = [];
  private warriors: Map<string, WarriorSprite> = new Map();
  private options: BoardContainerOptions;
  private previousPositions: Map<string, BoardPosition> = new Map();

  constructor(options: BoardContainerOptions) {
    super();
    this.options = options;

    // Background
    this.background = new Graphics();
    this.addChild(this.background);

    this.buildTiles();
    this.drawBackground();
  }

  /**
   * Whether to flip the board perspective (when local player is player2/guest)
   */
  private get flipPerspective(): boolean {
    return this.options.viewPlayers.localId === 'player2';
  }

  private drawBackground(): void {
    const { boardSize } = this.options.metrics;

    this.background.clear();
    this.background.roundRect(0, 0, boardSize, boardSize, 8);
    this.background.fill({ color: COLORS.BG_SECONDARY });
  }

  /**
   * Update the view players (called when perspective changes)
   */
  setViewPlayers(viewPlayers: ViewPlayers): void {
    this.options.viewPlayers = viewPlayers;
  }

  private buildTiles(): void {
    const { tileSize } = this.options.metrics;
    const flip = this.flipPerspective;

    // Clear existing tiles
    for (const row of this.tiles) {
      for (const tile of row) {
        tile.destroy();
      }
    }
    this.tiles = [];

    // Create 5x5 grid
    // Tiles are stored by GAME position but displayed at VISUAL position
    for (let gameRow = 0; gameRow < BOARD_SIZE; gameRow++) {
      this.tiles[gameRow] = [];
      for (let col = 0; col < BOARD_SIZE; col++) {
        const visualRow = getVisualRow(gameRow, flip);

        const tile = new TileSprite({
          position: { row: gameRow, col }, // Game position for click handling
          size: tileSize,
          onClick: this.options.onTileClick,
        });

        tile.x = col * (tileSize + TILE_GAP) + tileSize / 2;
        tile.y = visualRow * (tileSize + TILE_GAP) + tileSize / 2;

        this.addChild(tile);
        this.tiles[gameRow][col] = tile;
      }
    }
  }

  /**
   * Update the board with current game state
   */
  updateState(
    allWarriors: WarriorInstance[],
    selectedWarriorId: string | null,
    movePositions: BoardPosition[],
    dashPositions: BoardPosition[],
    attackPositions: BoardPosition[],
    previewAttackPositions: BoardPosition[]
  ): void {
    const { tileSize } = this.options.metrics;

    // Get selected warrior position for arrow calculation
    const selectedWarrior = selectedWarriorId
      ? allWarriors.find((w) => w.instanceId === selectedWarriorId)
      : null;
    const selectedPos = selectedWarrior?.position;

    // Reset all tiles to normal state and clear arrows
    for (const row of this.tiles) {
      for (const tile of row) {
        tile.setState('normal');
        tile.setArrow(null, null);
      }
    }

    // Set arrows for move positions (green arrows)
    // Use visual rows for arrow direction calculation so arrows point correctly when board is flipped
    const flip = this.flipPerspective;
    for (const pos of movePositions) {
      const tile = this.tiles[pos.row]?.[pos.col];
      if (tile && selectedPos) {
        const arrow = TileSprite.getArrowForDirection(
          getVisualRow(selectedPos.row, flip),
          selectedPos.col,
          getVisualRow(pos.row, flip),
          pos.col
        );
        tile.setArrow(arrow, 'move');
      }
    }

    // Set arrows for dash positions (blue arrows)
    for (const pos of dashPositions) {
      const tile = this.tiles[pos.row]?.[pos.col];
      if (tile && selectedPos) {
        const arrow = TileSprite.getArrowForDirection(
          getVisualRow(selectedPos.row, flip),
          selectedPos.col,
          getVisualRow(pos.row, flip),
          pos.col
        );
        tile.setArrow(arrow, 'dash');
      }
    }

    // Set attack tile states (red highlight)
    for (const pos of attackPositions) {
      this.tiles[pos.row]?.[pos.col]?.setState('attack');
    }

    // Set preview attack tile states
    for (const pos of previewAttackPositions) {
      this.tiles[pos.row]?.[pos.col]?.setState('preview-attack');
    }

    // Update warriors - only count warriors that are still on the board (have a position)
    const activeWarriorIds = new Set(
      allWarriors.filter((w) => w.position !== null).map((w) => w.instanceId)
    );

    // Remove defeated warriors with animation
    for (const [id, sprite] of this.warriors) {
      if (!activeWarriorIds.has(id)) {
        // Remove from map immediately to prevent duplicate animations
        this.warriors.delete(id);
        // Play defeat animation, then dispose
        sprite.playDefeatAnimation().then(() => {
          sprite.dispose();
        });
      }
    }

    // Add/update warriors
    for (const warrior of allWarriors) {
      if (!warrior.position) continue;

      // Convert game position to visual position
      const visualRow = getVisualRow(warrior.position.row, flip);
      const targetX = warrior.position.col * (tileSize + TILE_GAP) + tileSize / 2;
      const targetY = visualRow * (tileSize + TILE_GAP) + tileSize / 2;

      let sprite = this.warriors.get(warrior.instanceId);

      if (!sprite) {
        // Remote warriors (opponent) are rotated 180 degrees
        const isRemote = isRemoteWarrior(warrior, this.options.viewPlayers);

        // Create new warrior sprite
        sprite = new WarriorSprite({
          warrior,
          tileSize,
          isPlayer2: isRemote,
          onAttack: this.options.onAttack,
          onHeal: this.options.onHeal,
        });
        sprite.x = targetX;
        sprite.y = targetY;
        this.addChild(sprite);
        this.warriors.set(warrior.instanceId, sprite);
      } else {
        // Check if warrior moved
        const prevPos = this.previousPositions.get(warrior.instanceId);
        if (
          prevPos &&
          (prevPos.row !== warrior.position.row || prevPos.col !== warrior.position.col)
        ) {
          // Animate movement
          sprite.animateMoveTo(targetX, targetY);
        }
      }

      // Update selection state
      const isSelected = warrior.instanceId === selectedWarriorId;
      sprite.setSelected(isSelected);

      // Update health bar
      sprite.updateHealth(warrior.currentHealth);

      // Update readiness indicators (only visible when selected)
      const attackFrequency = warrior.definition.stats.attackFrequency;
      const healingFrequency = warrior.definition.stats.healingFrequency ?? 0;
      const isAttackReady = attackFrequency === 0 || warrior.attackCooldown >= attackFrequency;
      const healingPower = warrior.definition.stats.healingPower ?? 0;
      const isHealReady = healingPower > 0 && (healingFrequency === 0 || warrior.healCooldown >= healingFrequency);
      sprite.setReadinessIndicators(isAttackReady, isHealReady, warrior.attackCooldown, warrior.healCooldown);

      // Highlight selected warrior's tile
      if (warrior.instanceId === selectedWarriorId) {
        this.tiles[warrior.position.row]?.[warrior.position.col]?.setState('selected');
      }
    }

    // Store positions for next update
    this.previousPositions.clear();
    for (const warrior of allWarriors) {
      if (warrior.position) {
        this.previousPositions.set(warrior.instanceId, { ...warrior.position });
      }
    }
  }

  /**
   * Update layout when screen resizes
   */
  updateLayout(metrics: LayoutMetrics): void {
    this.options.metrics = metrics;
    const { tileSize, boardSize } = metrics;
    const flip = this.flipPerspective;

    // Redraw background
    this.background.clear();
    this.background.roundRect(0, 0, boardSize, boardSize, 8);
    this.background.fill({ color: COLORS.BG_SECONDARY });

    // Update tile positions and sizes
    for (let gameRow = 0; gameRow < BOARD_SIZE; gameRow++) {
      const visualRow = getVisualRow(gameRow, flip);
      for (let col = 0; col < BOARD_SIZE; col++) {
        const tile = this.tiles[gameRow]?.[col];
        if (tile) {
          tile.x = col * (tileSize + TILE_GAP) + tileSize / 2;
          tile.y = visualRow * (tileSize + TILE_GAP) + tileSize / 2;
          tile.setSize(tileSize);
        }
      }
    }

    // Update warrior positions
    for (const [_id, sprite] of this.warriors) {
      const warrior = sprite.getWarrior();
      if (warrior.position) {
        const visualRow = getVisualRow(warrior.position.row, flip);
        sprite.x = warrior.position.col * (tileSize + TILE_GAP) + tileSize / 2;
        sprite.y = visualRow * (tileSize + TILE_GAP) + tileSize / 2;
        sprite.setTileSize(tileSize);
      }
    }
  }

  /**
   * Get a warrior sprite by instance ID
   */
  getWarriorSprite(instanceId: string): WarriorSprite | undefined {
    return this.warriors.get(instanceId);
  }

  dispose(): void {
    for (const row of this.tiles) {
      for (const tile of row) {
        tile.destroy();
      }
    }
    for (const sprite of this.warriors.values()) {
      sprite.dispose();
    }
    this.warriors.clear();
    this.destroy({ children: true });
  }
}
